/**
 * Inventory and recipes contracts (M5 task 2; 03-modules.md §10, 04-data-model.md "inventory",
 * 05-api.md "Inventory and bar", 06-flows.md F7).
 *
 * Quantities are integer milli-units of the item's base unit (g, ml or pc): `qtyMilli: 2500`
 * is 2.5 of it. Money is integer paise. Cost is kept **per 1,000 base units** (`costPerKPaise`:
 * paise per kg, per litre, per 1,000 pieces) because the price of one gram is a fraction of a
 * paisa. The stock ledger is append-only and a balance is the sum of its rows.
 */
import { z } from 'zod';
import { businessDateSchema, isoTimestampSchema } from './common.js';
import { defineEvent } from './events.js';
import { uuidSchema } from './ids.js';
import { nonNegativePaiseSchema, paiseSchema } from './money.js';
import { REPORT_FORMATS } from './reports.js';

// ---------------------------------------------------------------------------
// Vocabulary

export const STOCK_KINDS = ['raw', 'liquor', 'finished'] as const;
export type StockKind = (typeof STOCK_KINDS)[number];

export const STOCK_BASE_UNITS = ['g', 'ml', 'pc'] as const;
export type StockBaseUnit = (typeof STOCK_BASE_UNITS)[number];

export const COST_METHODS = ['avg', 'last'] as const;
export type CostMethod = (typeof COST_METHODS)[number];

export const LEDGER_REASONS = [
  'sale',
  'void_return',
  'wastage',
  'breakage',
  'move_in',
  'move_out',
  'stock_in',
  'count_adjust',
  'opening',
] as const;
export type LedgerReason = (typeof LEDGER_REASONS)[number];

export const RECIPE_TARGET_TYPES = ['product', 'variant', 'addon'] as const;
export type RecipeTargetType = (typeof RECIPE_TARGET_TYPES)[number];

export const WASTAGE_REASONS = [
  'spoilage',
  'expired',
  'spillage',
  'prep_waste',
  'returned',
  'other',
] as const;
export type WastageReason = (typeof WASTAGE_REASONS)[number];

export const WASTAGE_STATUSES = ['approved', 'pending', 'rejected'] as const;
export type WastageStatus = (typeof WASTAGE_STATUSES)[number];

export const COUNT_STATUSES = ['open', 'submitted', 'approved', 'cancelled'] as const;
export type CountStatus = (typeof COUNT_STATUSES)[number];

/** Largest quantity a `numeric(14,3)` column holds, in milli. */
export const MAX_QTY_MILLI = 99_999_999_999_999;

export const stockQtyMilliSchema = z.int().min(-MAX_QTY_MILLI).max(MAX_QTY_MILLI);
export const stockPositiveQtyMilliSchema = z.int().min(1).max(MAX_QTY_MILLI);
export const stockNonNegativeQtyMilliSchema = z.int().min(0).max(MAX_QTY_MILLI);

/** Paise per 1,000 base units. */
export const costPerKSchema = nonNegativePaiseSchema;

const nameSchema = z.string().trim().min(1).max(80);
const noteSchema = z.string().trim().min(1).max(300);
export const unitCodeSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z][a-z0-9_]{0,19}$/, { message: 'Letters, digits and _, starting with a letter' });

const archivable = {
  archivedAt: isoTimestampSchema.nullable(),
  createdAt: isoTimestampSchema,
  updatedAt: isoTimestampSchema,
  version: z.int(),
};

const atLeastOne = (v: object) => Object.keys(v).length > 0;
const atLeastOneMsg = { message: 'Send at least one field to change' };

// ---------------------------------------------------------------------------
// Units, categories, brands

export const stockUnitSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  code: z.string(),
  name: z.string(),
  base: z.enum(STOCK_BASE_UNITS),
  factorMilli: z.int(),
  ...archivable,
});
export type StockUnitView = z.output<typeof stockUnitSchema>;

const unitFields = {
  code: unitCodeSchema,
  name: nameSchema,
  base: z.enum(STOCK_BASE_UNITS),
  /** How many base units one of this unit is, times 1000 (a 200 ml glass: 200000). */
  factorMilli: z.int().min(1).max(1_000_000_000),
};
export const createStockUnitSchema = z.object({ id: uuidSchema.optional(), ...unitFields });
export const updateStockUnitSchema = z
  .object({ name: unitFields.name, factorMilli: unitFields.factorMilli })
  .partial()
  .refine(atLeastOne, atLeastOneMsg);
export const stockUnitParamsSchema = z.object({ outletId: uuidSchema, unitId: uuidSchema });

export const standardUnitSchema = z.object({
  code: z.string(),
  base: z.enum(STOCK_BASE_UNITS),
  factorMilli: z.int(),
});
export const stockUnitListSchema = z.object({
  data: z.array(stockUnitSchema),
  /** Units every outlet has: g, kg, ml, l, pc, dozen, … */
  standard: z.array(standardUnitSchema),
});

export const stockLookupSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  name: z.string(),
  ...archivable,
});
export type StockLookupView = z.output<typeof stockLookupSchema>;
export const createStockLookupSchema = z.object({ id: uuidSchema.optional(), name: nameSchema });
export const updateStockLookupSchema = z
  .object({ name: nameSchema })
  .partial()
  .refine(atLeastOne, atLeastOneMsg);
export const stockCategoryParamsSchema = z.object({
  outletId: uuidSchema,
  categoryId: uuidSchema,
});
export const stockBrandParamsSchema = z.object({ outletId: uuidSchema, brandId: uuidSchema });

// ---------------------------------------------------------------------------
// Stock items

export const itemConversionSchema = z.object({
  unit: unitCodeSchema,
  /** Base units per one of this unit, times 1000 (a 750 ml bottle: 750000). */
  factorMilli: z.int().min(1).max(1_000_000_000_000),
});
export type ItemConversion = z.output<typeof itemConversionSchema>;

export const stockItemSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  name: z.string(),
  kind: z.enum(STOCK_KINDS),
  baseUnit: z.enum(STOCK_BASE_UNITS),
  purchaseUnit: z.string(),
  categoryId: uuidSchema.nullable(),
  brandId: uuidSchema.nullable(),
  sku: z.string().nullable(),
  reorderLevelMilli: z.int(),
  costMethod: z.enum(COST_METHODS),
  costPerKPaise: paiseSchema,
  conversions: z.array(itemConversionSchema),
  ...archivable,
});
export type StockItemView = z.output<typeof stockItemSchema>;

const itemFields = {
  name: nameSchema,
  kind: z.enum(STOCK_KINDS),
  baseUnit: z.enum(STOCK_BASE_UNITS),
  purchaseUnit: unitCodeSchema,
  categoryId: uuidSchema.nullable(),
  brandId: uuidSchema.nullable(),
  sku: z.string().trim().min(1).max(40).nullable(),
  reorderLevelMilli: stockNonNegativeQtyMilliSchema,
  costMethod: z.enum(COST_METHODS),
  /** The opening cost (paise per 1,000 base units); stock-in updates it after that. */
  costPerKPaise: costPerKSchema,
  /** Replaces the item's own conversions when sent. */
  conversions: z
    .array(itemConversionSchema)
    .max(20)
    .refine((v) => new Set(v.map((c) => c.unit)).size === v.length, {
      message: 'Each unit once',
    }),
};
export const createStockItemSchema = z.object({
  id: uuidSchema.optional(),
  name: itemFields.name,
  kind: itemFields.kind.optional(),
  baseUnit: itemFields.baseUnit,
  purchaseUnit: itemFields.purchaseUnit.optional(),
  categoryId: itemFields.categoryId.optional(),
  brandId: itemFields.brandId.optional(),
  sku: itemFields.sku.optional(),
  reorderLevelMilli: itemFields.reorderLevelMilli.optional(),
  costMethod: itemFields.costMethod.optional(),
  costPerKPaise: itemFields.costPerKPaise.optional(),
  conversions: itemFields.conversions.optional(),
});
export const updateStockItemSchema = z
  .object({ ...itemFields, baseUnit: itemFields.baseUnit })
  .partial()
  .refine(atLeastOne, atLeastOneMsg);
export const stockItemParamsSchema = z.object({ outletId: uuidSchema, itemId: uuidSchema });
export const stockItemListSchema = z.object({ data: z.array(stockItemSchema) });

// ---------------------------------------------------------------------------
// Vendors and storage sections

export const vendorSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  name: z.string(),
  gstin: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  address: z.string().nullable(),
  ...archivable,
});
export type VendorView = z.output<typeof vendorSchema>;

const vendorFields = {
  name: nameSchema,
  gstin: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/, { message: 'Not a GSTIN' })
    .nullable(),
  phone: z.string().trim().min(5).max(20).nullable(),
  email: z.string().trim().max(120).email().nullable(),
  address: z.string().trim().min(1).max(300).nullable(),
};
export const createVendorSchema = z.object({
  id: uuidSchema.optional(),
  name: vendorFields.name,
  gstin: vendorFields.gstin.optional(),
  phone: vendorFields.phone.optional(),
  email: vendorFields.email.optional(),
  address: vendorFields.address.optional(),
});
export const updateVendorSchema = z
  .object(vendorFields)
  .partial()
  .refine(atLeastOne, atLeastOneMsg);
export const vendorParamsSchema = z.object({ outletId: uuidSchema, vendorId: uuidSchema });
export const vendorListSchema = z.object({ data: z.array(vendorSchema) });

export const storageSectionSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  name: z.string(),
  isDefault: z.boolean(),
  ...archivable,
});
export type StorageSectionView = z.output<typeof storageSectionSchema>;
export const createStorageSectionSchema = z.object({
  id: uuidSchema.optional(),
  name: nameSchema,
  isDefault: z.boolean().optional(),
});
export const updateStorageSectionSchema = z
  .object({ name: nameSchema, isDefault: z.literal(true) })
  .partial()
  .refine(atLeastOne, atLeastOneMsg);
export const storageSectionParamsSchema = z.object({
  outletId: uuidSchema,
  sectionId: uuidSchema,
});
export const storageSectionListSchema = z.object({ data: z.array(storageSectionSchema) });

// ---------------------------------------------------------------------------
// Recipes

export const recipeLineSchema = z.object({
  stockItemId: uuidSchema,
  qtyMilli: stockPositiveQtyMilliSchema,
  unit: unitCodeSchema,
  /** Where it is taken from; null is the outlet's default storage section. */
  storageSectionId: uuidSchema.nullable(),
});
export type RecipeLineView = z.output<typeof recipeLineSchema>;

export const recipeVersionSchema = z.object({
  id: uuidSchema,
  versionNo: z.int(),
  effectiveFrom: isoTimestampSchema,
  createdBy: uuidSchema.nullable(),
  createdAt: isoTimestampSchema,
  lines: z.array(recipeLineSchema),
});
export type RecipeVersionView = z.output<typeof recipeVersionSchema>;

export const recipeSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  targetType: z.enum(RECIPE_TARGET_TYPES),
  targetId: uuidSchema,
  /** The version in force now (null before the first one starts). */
  currentVersionId: uuidSchema.nullable(),
  versions: z.array(recipeVersionSchema),
});
export type RecipeView = z.output<typeof recipeSchema>;

export const recipeSummarySchema = z.object({
  id: uuidSchema,
  targetType: z.enum(RECIPE_TARGET_TYPES),
  targetId: uuidSchema,
  versionCount: z.int(),
  currentLineCount: z.int(),
  currentVersionId: uuidSchema.nullable(),
  latestEffectiveFrom: isoTimestampSchema,
});
export const recipeListSchema = z.object({ data: z.array(recipeSummarySchema) });

export const recipeParamsSchema = z.object({
  outletId: uuidSchema,
  targetType: z.enum(RECIPE_TARGET_TYPES),
  targetId: uuidSchema,
});
export const recipeListQuerySchema = z.object({
  targetType: z.enum(RECIPE_TARGET_TYPES).optional(),
});

/** `PUT {o}/recipes/{targetType}/{targetId}`: a new version (an empty list: "uses no stock"). */
export const setRecipeSchema = z.object({
  versionId: uuidSchema.optional(),
  /** When it starts applying (default: now). Orders placed before it keep the older version. */
  effectiveFrom: isoTimestampSchema.optional(),
  lines: z
    .array(recipeLineSchema)
    .max(60)
    .refine(
      (v) =>
        new Set(v.map((l) => `${l.stockItemId}|${l.storageSectionId ?? ''}`)).size === v.length,
      { message: 'Each item once per storage section' },
    ),
});

// ---------------------------------------------------------------------------
// Stock in (manual purchase)

export const stockInLineInputSchema = z.object({
  stockItemId: uuidSchema,
  qtyMilli: stockPositiveQtyMilliSchema,
  /** The unit the quantity and cost are in (default: the item's purchase unit). */
  unit: unitCodeSchema.optional(),
  /** Paise per one of `unit`. */
  unitCostPaise: nonNegativePaiseSchema,
  batchNo: z.string().trim().min(1).max(40).optional(),
  expiry: businessDateSchema.optional(),
  storageSectionId: uuidSchema.optional(),
});

export const recordStockInSchema = z.object({
  id: uuidSchema.optional(),
  vendorId: uuidSchema,
  invoiceNo: z.string().trim().min(1).max(40),
  invoiceDate: businessDateSchema,
  note: noteSchema.optional(),
  lines: z.array(stockInLineInputSchema).min(1).max(100),
});

export const stockInLineSchema = z.object({
  id: uuidSchema,
  stockItemId: uuidSchema,
  itemName: z.string(),
  qtyMilli: z.int(),
  unit: z.string(),
  qtyBaseMilli: z.int(),
  unitCostPaise: paiseSchema,
  lineTotalPaise: paiseSchema,
  batchNo: z.string().nullable(),
  expiry: businessDateSchema.nullable(),
  storageSectionId: uuidSchema,
});
export const stockInSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  vendorId: uuidSchema,
  vendorName: z.string(),
  invoiceNo: z.string(),
  invoiceDate: businessDateSchema,
  businessDate: businessDateSchema,
  totalPaise: paiseSchema,
  note: z.string().nullable(),
  createdBy: uuidSchema.nullable(),
  createdAt: isoTimestampSchema,
  lines: z.array(stockInLineSchema),
});
export type StockInView = z.output<typeof stockInSchema>;
export const stockInParamsSchema = z.object({ outletId: uuidSchema, stockInId: uuidSchema });
export const stockInListQuerySchema = z.object({
  vendorId: uuidSchema.optional(),
  from: businessDateSchema.optional(),
  to: businessDateSchema.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).max(1_000_000).default(0),
});
export const stockInListSchema = z.object({
  data: z.array(stockInSchema.omit({ lines: true }).extend({ lineCount: z.int() })),
  nextOffset: z.int().nullable(),
});

// ---------------------------------------------------------------------------
// Moves, wastage, counts

export const moveStockSchema = z
  .object({
    id: uuidSchema.optional(),
    stockItemId: uuidSchema,
    fromSectionId: uuidSchema,
    toSectionId: uuidSchema,
    qtyMilli: stockPositiveQtyMilliSchema,
    unit: unitCodeSchema.optional(),
    note: noteSchema.optional(),
  })
  .refine((v) => v.fromSectionId !== v.toSectionId, {
    message: 'Choose two different storage sections',
    path: ['toSectionId'],
  });
export const stockMoveSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  stockItemId: uuidSchema,
  itemName: z.string(),
  fromSectionId: uuidSchema,
  toSectionId: uuidSchema,
  qtyMilli: z.int(),
  note: z.string().nullable(),
  businessDate: businessDateSchema,
  createdBy: uuidSchema.nullable(),
  createdAt: isoTimestampSchema,
});
export type StockMoveView = z.output<typeof stockMoveSchema>;
export const stockMoveListSchema = z.object({
  data: z.array(stockMoveSchema),
  nextOffset: z.int().nullable(),
});
export const stockPagedQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).max(1_000_000).default(0),
});

export const recordWastageSchema = z.object({
  id: uuidSchema.optional(),
  stockItemId: uuidSchema,
  storageSectionId: uuidSchema.optional(),
  qtyMilli: stockPositiveQtyMilliSchema,
  unit: unitCodeSchema.optional(),
  reason: z.enum(WASTAGE_REASONS),
  note: noteSchema.optional(),
});
export const decideWastageSchema = z.object({ note: noteSchema.optional() });

export const wastageSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  stockItemId: uuidSchema,
  itemName: z.string(),
  baseUnit: z.enum(STOCK_BASE_UNITS),
  storageSectionId: uuidSchema,
  qtyMilli: z.int(),
  reason: z.enum(WASTAGE_REASONS),
  note: z.string().nullable(),
  valuePaise: paiseSchema,
  status: z.enum(WASTAGE_STATUSES),
  businessDate: businessDateSchema,
  recordedBy: uuidSchema.nullable(),
  decidedBy: uuidSchema.nullable(),
  decidedAt: isoTimestampSchema.nullable(),
  createdAt: isoTimestampSchema,
});
export type WastageView = z.output<typeof wastageSchema>;
export const wastageParamsSchema = z.object({ outletId: uuidSchema, wastageId: uuidSchema });

export const startCountSchema = z.object({
  id: uuidSchema.optional(),
  storageSectionId: uuidSchema,
  /** Count only these items (default: every live item). */
  stockItemIds: z.array(uuidSchema).min(1).max(500).optional(),
  note: noteSchema.optional(),
});
export const submitCountSchema = z.object({
  lines: z
    .array(
      z.object({
        stockItemId: uuidSchema,
        countedQtyMilli: stockNonNegativeQtyMilliSchema,
      }),
    )
    .min(1)
    .max(500),
});
export const approveCountSchema = z.object({ note: noteSchema.optional() });

export const stockCountLineSchema = z.object({
  stockItemId: uuidSchema,
  itemName: z.string(),
  baseUnit: z.enum(STOCK_BASE_UNITS),
  systemQtyMilli: z.int(),
  countedQtyMilli: z.int().nullable(),
  varianceMilli: z.int().nullable(),
  varianceValuePaise: paiseSchema.nullable(),
});
export const stockCountSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  storageSectionId: uuidSchema,
  status: z.enum(COUNT_STATUSES),
  note: z.string().nullable(),
  businessDate: businessDateSchema,
  startedBy: uuidSchema.nullable(),
  submittedBy: uuidSchema.nullable(),
  approvedBy: uuidSchema.nullable(),
  startedAt: isoTimestampSchema,
  submittedAt: isoTimestampSchema.nullable(),
  decidedAt: isoTimestampSchema.nullable(),
  lines: z.array(stockCountLineSchema),
  /** Net value of the variances (negative: stock is short). */
  varianceValuePaise: paiseSchema,
  version: z.int(),
});
export type StockCountView = z.output<typeof stockCountSchema>;
export const stockCountSummarySchema = stockCountSchema
  .omit({ lines: true })
  .extend({ lineCount: z.int(), varianceLineCount: z.int() });
export const stockCountParamsSchema = z.object({ outletId: uuidSchema, countId: uuidSchema });
export const stockCountListQuerySchema = z.object({
  status: z.enum(COUNT_STATUSES).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).max(1_000_000).default(0),
});
export const stockCountListSchema = z.object({
  data: z.array(stockCountSummarySchema),
  nextOffset: z.int().nullable(),
});

// ---------------------------------------------------------------------------
// Reports

export const STOCK_REPORT_KEYS = ['stock', 'ledger', 'consumption', 'wastage'] as const;
export type StockReportKey = (typeof STOCK_REPORT_KEYS)[number];

const queryInt = (v: unknown) => (typeof v === 'string' && /^\d+$/.test(v) ? Number(v) : v);

const formatField = z.enum(REPORT_FORMATS).default('json');

/** `GET {o}/stock`: what is on hand now. */
export const stockQuerySchema = z.object({
  sectionId: uuidSchema.optional(),
  belowReorder: z.enum(['true', 'false']).optional(),
  q: z.string().trim().min(1).max(60).optional(),
  format: formatField,
});
export const stockRowSchema = z.object({
  stockItemId: uuidSchema,
  name: z.string(),
  kind: z.enum(STOCK_KINDS),
  baseUnit: z.enum(STOCK_BASE_UNITS),
  categoryId: uuidSchema.nullable(),
  reorderLevelMilli: z.int(),
  qtyMilli: z.int(),
  costPerKPaise: paiseSchema,
  valuePaise: paiseSchema,
  low: z.boolean(),
  negative: z.boolean(),
  sections: z.array(
    z.object({ storageSectionId: uuidSchema, name: z.string(), qtyMilli: z.int() }),
  ),
});
export const stockReportSchema = z.object({
  outletId: uuidSchema,
  outletName: z.string(),
  asOf: isoTimestampSchema,
  rows: z.array(stockRowSchema),
  totals: z.object({
    items: z.int(),
    valuePaise: paiseSchema,
    lowCount: z.int(),
    negativeCount: z.int(),
  }),
});
export type StockReport = z.output<typeof stockReportSchema>;

/** `GET {o}/stock/ledger`. */
export const ledgerQuerySchema = z.object({
  from: businessDateSchema,
  to: businessDateSchema,
  itemId: uuidSchema.optional(),
  sectionId: uuidSchema.optional(),
  reason: z.enum(LEDGER_REASONS).optional(),
  limit: z.preprocess(queryInt, z.number().int().min(1).max(5000).optional()),
  offset: z.preprocess(queryInt, z.number().int().min(0).max(1_000_000).optional()),
  format: formatField,
});
export const ledgerRowSchema = z.object({
  id: uuidSchema,
  seq: z.int(),
  at: isoTimestampSchema,
  businessDate: businessDateSchema,
  stockItemId: uuidSchema,
  itemName: z.string(),
  baseUnit: z.enum(STOCK_BASE_UNITS),
  storageSectionId: uuidSchema,
  sectionName: z.string(),
  qtyDeltaMilli: z.int(),
  reason: z.enum(LEDGER_REASONS),
  refType: z.string().nullable(),
  refId: uuidSchema.nullable(),
  batchNo: z.string().nullable(),
  costPerKPaise: paiseSchema.nullable(),
  valuePaise: paiseSchema.nullable(),
  actorId: uuidSchema.nullable(),
  note: z.string().nullable(),
  /** The balance of the item (in the section when one is chosen) after this row; item filter only. */
  balanceAfterMilli: z.int().nullable(),
});
export const ledgerReportSchema = z.object({
  outletId: uuidSchema,
  outletName: z.string(),
  from: businessDateSchema,
  to: businessDateSchema,
  itemId: uuidSchema.nullable(),
  sectionId: uuidSchema.nullable(),
  openingMilli: z.int().nullable(),
  closingMilli: z.int().nullable(),
  rows: z.array(ledgerRowSchema),
  nextOffset: z.int().nullable(),
});
export type LedgerReport = z.output<typeof ledgerReportSchema>;

/** `GET {o}/stock/consumption`. */
export const consumptionQuerySchema = z.object({
  from: businessDateSchema,
  to: businessDateSchema,
  format: formatField,
});
export const consumptionRowSchema = z.object({
  stockItemId: uuidSchema,
  name: z.string(),
  baseUnit: z.enum(STOCK_BASE_UNITS),
  openingMilli: z.int(),
  receivedMilli: z.int(),
  /** Used by sales, net of voids. */
  consumedMilli: z.int(),
  /** Wastage and breakage. */
  wastedMilli: z.int(),
  /** Count adjustments and opening balances. */
  adjustedMilli: z.int(),
  closingMilli: z.int(),
  consumedValuePaise: paiseSchema,
  wastedValuePaise: paiseSchema,
});
export const consumptionReportSchema = z.object({
  outletId: uuidSchema,
  outletName: z.string(),
  from: businessDateSchema,
  to: businessDateSchema,
  rows: z.array(consumptionRowSchema),
  totals: z.object({
    consumedValuePaise: paiseSchema,
    wastedValuePaise: paiseSchema,
    receivedValuePaise: paiseSchema,
  }),
});
export type ConsumptionReport = z.output<typeof consumptionReportSchema>;

/** `GET {o}/wastage`. */
export const wastageQuerySchema = z.object({
  from: businessDateSchema,
  to: businessDateSchema,
  status: z.enum(WASTAGE_STATUSES).optional(),
  format: formatField,
});
export const wastageReportSchema = z.object({
  outletId: uuidSchema,
  outletName: z.string(),
  from: businessDateSchema,
  to: businessDateSchema,
  rows: z.array(wastageSchema),
  byReason: z.array(
    z.object({ reason: z.enum(WASTAGE_REASONS), count: z.int(), valuePaise: paiseSchema }),
  ),
  /** Approved wastage only. */
  totalValuePaise: paiseSchema,
  pendingCount: z.int(),
});
export type WastageReport = z.output<typeof wastageReportSchema>;

// ---------------------------------------------------------------------------
// Events

const MASTER_CHANGES = z.enum(['created', 'updated', 'archived']);
const lookupKind = z.enum(['category', 'brand']);

export const stockChangedV1 = defineEvent(
  'stock.changed',
  1,
  z.object({
    stockItemId: uuidSchema,
    storageSectionId: uuidSchema,
    /** The balance after the change (milli of the base unit). */
    qtyMilli: z.int(),
    deltaMilli: z.int(),
    reason: z.enum(LEDGER_REASONS),
  }),
);

/** Emitted when an item's balance crosses its reorder level downwards (alerting is Later). */
export const stockLowV1 = defineEvent(
  'stock.low',
  1,
  z.object({
    stockItemId: uuidSchema,
    totalQtyMilli: z.int(),
    reorderLevelMilli: z.int(),
  }),
);

export const stockItemSavedV1 = defineEvent(
  'stock_item.saved',
  1,
  z.object({ stockItemId: uuidSchema, change: MASTER_CHANGES, version: z.int() }),
);
export const stockLookupSavedV1 = defineEvent(
  'stock_lookup.saved',
  1,
  z.object({ kind: lookupKind, id: uuidSchema, change: MASTER_CHANGES, version: z.int() }),
);
export const stockUnitSavedV1 = defineEvent(
  'stock_unit.saved',
  1,
  z.object({ unitId: uuidSchema, change: MASTER_CHANGES, version: z.int() }),
);
export const vendorSavedV1 = defineEvent(
  'vendor.saved',
  1,
  z.object({ vendorId: uuidSchema, change: MASTER_CHANGES, version: z.int() }),
);
export const storageSectionSavedV1 = defineEvent(
  'storage_section.saved',
  1,
  z.object({ storageSectionId: uuidSchema, change: MASTER_CHANGES, version: z.int() }),
);
export const recipeSetV1 = defineEvent(
  'recipe.set',
  1,
  z.object({
    recipeId: uuidSchema,
    targetType: z.enum(RECIPE_TARGET_TYPES),
    targetId: uuidSchema,
    versionId: uuidSchema,
    versionNo: z.int(),
    effectiveFrom: isoTimestampSchema,
    lineCount: z.int(),
  }),
);
export const stockInRecordedV1 = defineEvent(
  'stock_in.recorded',
  1,
  z.object({
    stockInId: uuidSchema,
    vendorId: uuidSchema,
    invoiceNo: z.string(),
    totalPaise: paiseSchema,
    lineCount: z.int(),
  }),
);
export const stockMovedV1 = defineEvent(
  'stock.moved',
  1,
  z.object({
    moveId: uuidSchema,
    stockItemId: uuidSchema,
    fromSectionId: uuidSchema,
    toSectionId: uuidSchema,
    qtyMilli: z.int(),
  }),
);
export const wastageRecordedV1 = defineEvent(
  'wastage.recorded',
  1,
  z.object({
    wastageId: uuidSchema,
    stockItemId: uuidSchema,
    qtyMilli: z.int(),
    valuePaise: paiseSchema,
    reason: z.enum(WASTAGE_REASONS),
    status: z.enum(WASTAGE_STATUSES),
  }),
);
export const wastageDecidedV1 = defineEvent(
  'wastage.decided',
  1,
  z.object({
    wastageId: uuidSchema,
    stockItemId: uuidSchema,
    decision: z.enum(['approved', 'rejected']),
    valuePaise: paiseSchema,
  }),
);
export const stockCountChangedV1 = defineEvent(
  'stock_count.changed',
  1,
  z.object({
    countId: uuidSchema,
    storageSectionId: uuidSchema,
    status: z.enum(COUNT_STATUSES),
    varianceLines: z.int(),
    varianceValuePaise: paiseSchema,
  }),
);
/** An order's consumption was brought in line with the ledger (sale or void return). */
export const stockDeductedV1 = defineEvent(
  'stock.deducted',
  1,
  z.object({
    orderId: uuidSchema,
    trigger: z.enum(['kot', 'settle']),
    rows: z.int(),
    returned: z.int(),
  }),
);

export const INVENTORY_EVENTS = [
  stockChangedV1,
  stockLowV1,
  stockItemSavedV1,
  stockLookupSavedV1,
  stockUnitSavedV1,
  vendorSavedV1,
  storageSectionSavedV1,
  recipeSetV1,
  stockInRecordedV1,
  stockMovedV1,
  wastageRecordedV1,
  wastageDecidedV1,
  stockCountChangedV1,
  stockDeductedV1,
] as const;
